import { Link2, Share2 } from 'lucide-react'

import { LinkedInMonoIcon } from '@/components/ui/brand-social-icons'

import { Button } from '@/components/ui/button'
import { ButtonDropdownChevron } from '@/components/ui/button-dropdown-chevron'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { copyPublicUrl } from '@/utils/copy-public-url'

export type PublicShareMenuItemsProps = {
  url: string
  copyLabel?: string
  linkedInLabel?: string
  onShareLinkedIn?: () => void
  disabled?: boolean
}

/** Menu rows for Copy link and optional Share to LinkedIn — embed in parent overflow menus. */
export function PublicShareMenuItems({
  url,
  copyLabel = 'Copy link',
  linkedInLabel = 'Share to LinkedIn',
  onShareLinkedIn,
  disabled = false,
}: PublicShareMenuItemsProps) {
  return (
    <>
      <DropdownMenuItem
        className="gap-2"
        disabled={disabled}
        onClick={() => void copyPublicUrl(url)}
      >
        <Link2 className="h-4 w-4" aria-hidden />
        {copyLabel}
      </DropdownMenuItem>
      {onShareLinkedIn ? (
        <DropdownMenuItem className="gap-2" disabled={disabled} onClick={onShareLinkedIn}>
          <LinkedInMonoIcon className="h-4 w-4" />
          {linkedInLabel}
        </DropdownMenuItem>
      ) : null}
    </>
  )
}

type PublicShareMenuProps = PublicShareMenuItemsProps

/** Share dropdown when multiple share actions exist (Copy + LinkedIn). */
export function PublicShareMenu({
  url,
  copyLabel = 'Copy link',
  linkedInLabel = 'Share to LinkedIn',
  onShareLinkedIn,
  disabled = false,
}: PublicShareMenuProps) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled}
          className="gap-1.5"
          aria-label="Share options"
        >
          <Share2 className="h-4 w-4" aria-hidden />
          Share
          <ButtonDropdownChevron />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <PublicShareMenuItems
          url={url}
          copyLabel={copyLabel}
          linkedInLabel={linkedInLabel}
          onShareLinkedIn={onShareLinkedIn}
          disabled={disabled}
        />
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
